import { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
    ContextHolder, 
    useAuth as useFronteggAuth,
    useLoginWithRedirect,
} from '@frontegg/react';

import getUserDataOnLogin from '../hooks/user/getUserDataOnLogin';
import ActionConfirmation from '../components/elements/ActionConfirmation';
import { SESSION_KEY_CLIENT_ID, SESSION_KEY_JWT, SESSION_KEY_LOCATION_ID } from '../constants';
import type { ActionConfirmationVariant } from '../types';

import { useUser } from './UserContext';
import { useCart } from './CartContext';
import { useScrollToConfirmation } from '../hooks/useScrollToConfirmation';

interface AuthContextType {
    isAuthenticated: boolean;
    /** True while user data is being fetched after login (e.g. getUserDataOnLogin). */
    isUserDataLoading: boolean;
    handleLoginClick: () => void;
    loginUser: ( userToken: string ) => Promise<boolean>;
    logoutUser: () => void;
    sessionExpired: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

interface AuthProviderProps {
    children: React.ReactNode;
}

const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
    const navigate = useNavigate();
    const { resetUserContext, updateUserAndSession } = useUser();
    const { resetCartContext, fetchCart, applyPaymentMethod } = useCart();
    const { user, isAuthenticated: isFronteggAuthenticated } = useFronteggAuth();
    const loginWithRedirect = useLoginWithRedirect();

    const [ isAuthenticated, setIsAuthenticated ] = useState(false);
    const [ isUserDataLoading, setIsUserDataLoading ] = useState(false);
    const [ confirmation, setConfirmation ] = useState<{ visible: boolean; variant: ActionConfirmationVariant; message: string }>({ visible: false, variant: 'info', message: '' });


    useEffect(() => {
        const jwtToken = localStorage.getItem(SESSION_KEY_JWT);
        setIsAuthenticated(!!jwtToken);

        const interceptor = axios.interceptors.response.use(
            response => response,
            error => {
                if (error.response && error.response.status === 401) {
                    const requestUrl = error.response.config.url;
                    if (requestUrl && requestUrl.includes('cart')) {
                        sessionExpired();
                    }
                }
                return Promise.reject(error);
            }
        );

        return () => {
            axios.interceptors.response.eject(interceptor);
        };
    }, [navigate]);

	useEffect(() => {
        if (isFronteggAuthenticated) {
            if (user?.accessToken) {
                localStorage.setItem(SESSION_KEY_JWT, user.accessToken);
                loginUser(user.accessToken);
            } else {
                clearSession();
                setConfirmation({ visible: true, variant: 'error', message: 'An error occurred while logging in.' });
            }
        }
    }, [ isFronteggAuthenticated, loginWithRedirect ]);

    const handleLoginClick = () => {
        if (isFronteggAuthenticated) {
            logoutUser();
        } else {
            window.localStorage.setItem('FRONTEGG_AFTER_AUTH_REDIRECT_URL', window.location.href);
            loginWithRedirect();
        }
    };

    const loginUser = async ( accessToken: string ) => {
        setIsAuthenticated(true);
        setIsUserDataLoading(true);
        try {
            const { success, message } = await getUserDataOnLogin(accessToken, updateUserAndSession, fetchCart, applyPaymentMethod);
            if (!success) {
                setConfirmation({ visible: true, variant: 'error', message: `An error occurred while logging in: ${message}` });
                clearSession();
            }
            return success;
        } finally {
            setIsUserDataLoading(false);
        }
    };

    const sessionExpired = () => {
        clearSession();
        setConfirmation({ visible: true, variant: 'error', message: 'Session has expired.' });
    };

    const logoutUser = () => {
        clearSession();
        setConfirmation({ visible: true, variant: 'info', message: 'Successfully logged out.' });

        const baseUrl = ContextHolder.getContext().baseUrl;
        window.location.href = `${baseUrl}/oauth/logout?post_logout_redirect_uri=${window.location.href}`;
    };

    const clearSession = () => {
        const clientIdFromStorage = localStorage.getItem(SESSION_KEY_CLIENT_ID) as string;
        const locationIdFromStorage = localStorage.getItem(SESSION_KEY_LOCATION_ID) as string;
        resetUserContext();
        resetCartContext();
        localStorage.clear();
        localStorage.setItem(SESSION_KEY_CLIENT_ID, clientIdFromStorage);
        localStorage.setItem(SESSION_KEY_LOCATION_ID, locationIdFromStorage);
        setIsAuthenticated(false);
        navigate('/');
    };

    const handleDismissConfirmation = () => {
        setConfirmation((c) => ({ ...c, visible: false }));
    };

    useScrollToConfirmation(confirmation.visible, confirmation.message, 'auth-confirmation');

    const contextValue = {
        isAuthenticated,
        isUserDataLoading,
        handleLoginClick,
        loginUser,
        logoutUser,
        sessionExpired
    };

    return <AuthContext.Provider value={contextValue}>{children}
        <ActionConfirmation
            id="auth-confirmation"
            visible={confirmation.visible}
            variant={confirmation.variant}
            message={confirmation.message}
            onDismiss={handleDismissConfirmation}
        />
    </AuthContext.Provider>;
};

export { AuthProvider, useAuth };
